import React, {useMemo, useRef} from 'react';
import {classes, style} from './doppe-action-type-events-widget.st.css';
import {listViewCreateDataSource, useObservableValue} from '@wix/devzai-utils-react';
import {
    DoppeActionType,
    useIsWidgetRenderedInPage,
    useWidgetRenderingContext
} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetItemList,
    onListItemClick
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-list-view-item';
import {
    doppeHideableValueGetValue,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsHidden,
    doppeHideableValueIsVisibleAndNotEqualValue,
    doppeHideableValueToggleHidden
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionListItemClickBehaviour,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsAvailable,
    doppeExternalEventProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {arraySkipTake, evaluateFunction, iterableMapToArray, ObservableValue} from '@wix/devzai-utils-common';
import {convertDoppeWixEventToEventsSettings, DoppeActionTypeEvents} from '../doppe-action-type-events';
import {
    DoppeActionTypeEventBindableWidget
} from '../../doppe-action-type-event/doppe-action-type-event-widget/doppe-action-type-event-widget';
import {DoppeEventsExternalDataSourceType} from '../../../client-server-common/types/doppe-events-external-data-source';
import {DoppeAutoEventType} from '../../../client-server-common/types/doppe-auto-event-type';
import {DoppeViewerActionContext} from '../../../components/doppe-viewer-action-context/doppe-viewer-action-context';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {useDoppeViewerLocales} from "../../../client/doppe-viewer-locales/doppe-viewer-locales";

export const DoppeActionTypeEventsWidget = React.memo(function DoppeActionTypeEventsWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeEvents.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        renderingInnerStateController,
        observableCurrentItemIdToRender = new ObservableValue<string | null>(null)
    } = useWidgetRenderingContext();

    const {
        events,
        layout,
        itemClickBehaviour,
        showButton,

        externalDataSource,
        externalDataSourceItemsCount,

        previewShowCoupon,
        previewShowPrice,
        previewShowDescription,
        previewShowTitle,
        previewEventButtonText,

        itemShowDescription,
        itemShowImage,
        itemShowTitle,
        itemShowDate,
    } = actionSettings;

    const bymoPageContextProps = useBymoPageContextProps();

    const displayedEvents = useMemo(() => {
        if (externalDataSource === null) {
            return events;
        } else {

            const externalEvents = evaluateFunction(() => {
                switch (externalDataSource.type) {
                    case DoppeEventsExternalDataSourceType.UpcomingAndOldWixEvents: {
                        return bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, {
                            businessEntity: DoppeBusinessEntity.WixAutoEventsList,
                            id: DoppeAutoEventType.UpcomingAndOld
                        }) ?? []
                    }
                    case DoppeEventsExternalDataSourceType.UpcomingWixEvents: {
                        return bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, {
                            businessEntity: DoppeBusinessEntity.WixAutoEventsList,
                            id: DoppeAutoEventType.Upcoming
                        }) ?? []
                    }
                    case DoppeEventsExternalDataSourceType.OldWixEvents: {
                        return bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, {
                            businessEntity: DoppeBusinessEntity.WixAutoEventsList,
                            id: DoppeAutoEventType.Old
                        }) ?? []
                    }
                }
            })

            return arraySkipTake(externalEvents, 0, externalDataSourceItemsCount).map(doppeDtoEvent => {
                return {
                    ...convertDoppeWixEventToEventsSettings(doppeDtoEvent),
                    // We override the provider and the id in the provider since the values that are fetched
                    // here are already synced with the external provider.
                    eventProvider: null,
                    eventProviderId: null,
                } satisfies DoppeActionTypeEvents.EventSettings
            });
        }
    }, [externalDataSource, externalDataSourceItemsCount, events, bymoPageContextProps])


    const expandedEventId = useObservableValue(observableCurrentItemIdToRender);

    const currentEventToShow = useMemo(
        () => displayedEvents.find(event => event.id === expandedEventId) ?? null,
        [displayedEvents, expandedEventId]);


    const existingEvents = useMemo(() => {
        return iterableMapToArray(displayedEvents, (event, skip) => {

            const {
                eventProviderId,
                eventProvider
            } = event;

            if (eventProvider !== null && eventProviderId !== null) {
                const boundEvent = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalEventProviderResolveBusinessEntityReference(eventProvider, eventProviderId)
                );

                if (doppeBusinessEntityDtoIsAvailable(boundEvent)) {
                    return {
                        ...event,
                        businessEntityDto: boundEvent
                    }
                } else {
                    return skip;
                }
            } else {
                return {
                    ...event,
                    businessEntityDto: undefined
                }
            }
        })
    }, [displayedEvents, bymoPageContextProps]);

    const eventsListViewDataSource = useMemo(() => {
        return listViewCreateDataSource(existingEvents, {
            idFunction: item => item.id
        });
    }, [existingEvents]);


    const rootElementRef = useRef(null);
    const doppeViewerLocales = useDoppeViewerLocales();

    const isRenderedInPage = useIsWidgetRenderedInPage();

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
            ref={rootElementRef}
        >
            {evaluateFunction(() => {
                if (!currentEventToShow) {
                    if (eventsListViewDataSource.length === 0) {
                        return (
                            <DoppeActionUnavailablePreview
                                reason={DoppeActionUnavailableReason.ContentUnavailable}
                            />
                        )
                    } else {
                        return (
                            <DoppeActionTypeWidgetItemList
                                className={style(classes.listContent)}
                                actionContext={actionContext}
                                layout={layout}
                                listTitle={actionSettings.mainTitle}
                                media={doppeHideableValueGetVisibleValue(actionSettings.mainImage, undefined)}
                                hasSeeMoreButton={showButton}
                                onItemClick={eventData => {
                                    onListItemClick({
                                        itemClickBehaviour: !isRenderedInPage && itemClickBehaviour === DoppeActionListItemClickBehaviour.PreviewItemAsPopup ? DoppeActionListItemClickBehaviour.PreviewItem : itemClickBehaviour,
                                        renderingInnerStateController,
                                        observableCurrentItemIdToRender,
                                        rootElementRef,
                                        actionContext: actionContext as DoppeViewerActionContext<any>,
                                        itemId: eventData.id,
                                        itemIndex: displayedEvents.findIndex(item => item.id === eventData.id),
                                        itemLink: eventData.eventLink,
                                        couponCode: eventData.couponCode,
                                        viewerLang: doppeViewerLocales,
                                    })
                                }}
                                renderItem={(dataItem, layout, index) => {
                                    const {
                                        eventImages,
                                        eventTitle,
                                        description,
                                        ribbon,
                                        eventDate
                                        // businessEntityDto,
                                    } = dataItem;

                                    const isDateHidden = doppeHideableValueIsHidden(eventDate);

                                    return (
                                        <DoppeActionTypeListViewItem
                                            actionContext={actionContext as DoppeViewerActionContext<any>}
                                            innerItemId={dataItem.id}
                                            innerItemIndex={index}
                                            title={eventTitle}
                                            description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                                            image={eventImages?.[0] ?? null}
                                            layout={layout}
                                            date={isDateHidden || !itemShowDate ? undefined : eventDate}
                                            isOneItem={eventsListViewDataSource.length === 1}
                                            isLink={itemClickBehaviour === DoppeActionListItemClickBehaviour.OpenLink}
                                            ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? doppeHideableValueGetValue(ribbon) : undefined}
                                            hideImageWhenPossible={!itemShowImage}
                                            hideTitleWhenPossible={!itemShowTitle}
                                            hideDescriptionWhenPossible={!itemShowDescription}
                                        />
                                    )
                                }}
                                dataSourceItems={eventsListViewDataSource}
                            />
                        )
                    }
                }
                else {
                    return (
                        <DoppeActionTypeEventBindableWidget
                            actionSettings={{
                                eventImages: currentEventToShow.eventImages,
                                couponCode: doppeHideableValueToggleHidden(
                                    currentEventToShow.couponCode,
                                    isHidden => isHidden ? true : !previewShowCoupon
                                ),
                                description: doppeHideableValueToggleHidden(
                                    currentEventToShow.description,
                                    isHidden => isHidden ? true : !previewShowDescription
                                ),
                                eventTitle: doppeHideableValueToggleHidden(
                                    currentEventToShow.eventTitle,
                                    isHidden => isHidden ? true : !previewShowTitle
                                ),
                                eventButtonText: doppeHideableValueGetVisibleValue(
                                    currentEventToShow.eventButtonText,
                                    previewEventButtonText
                                ),
                                priceData: doppeHideableValueToggleHidden(
                                    currentEventToShow.priceData,
                                    isHidden => isHidden ? true : !previewShowPrice
                                ),
                                eventLink: currentEventToShow.eventLink,
                                eventLocation: currentEventToShow.eventLocation,
                                eventDate: currentEventToShow.eventDate,
                                eventTime: currentEventToShow.eventTime,
                                ribbon: currentEventToShow.ribbon,
                                eventProvider: currentEventToShow.eventProvider ?? null,
                                eventProviderId: currentEventToShow.eventProviderId ?? null,
                            }}
                            reportGoalAchieved={() => {
                                actionContext.reportGoalAchieved(
                                    currentEventToShow?.id,
                                    existingEvents.findIndex(item => item.id === currentEventToShow?.id)
                                );
                            }}
                            actionId={actionContext.action.id}
                        />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});


